import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import { useSmartInsights } from './SmartInsightsContext';
import './index.css';
import Sidebar from './components/Sidebar';
import FiltersBar, { DatePreset } from './components/FiltersBar';
import KPICard from './components/KPICard';
import RevenueExpensesChart from './components/RevenueExpensesChart';
import ExpenseBreakdownChart from './components/ExpenseBreakdownChart';
import CashFlowChart from './components/CashFlowChart';
import NetProfitChart from './components/NetProfitChart';
import TransactionTable from './components/TransactionTable';
import AccountsPayableReceivable from './components/AccountsPayableReceivable';
import RevenueDetailPage from './components/RevenueDetailPage';
import ExpensesDetailPage from './components/ExpensesDetailPage';
import BalanceSheetPage from './components/BalanceSheetPage';
import CashFlowPage from './components/CashFlowPage';
import ProfitLossPage from './components/ProfitLossPage';
import ForecastPage from './components/ForecastPage';
import BEPPage from './components/BEPPage';
import GLLookupPage from './components/GLLookupPage';
import DatabasePage from './components/DatabasePage';
import ExecutiveSummaryPage from './components/ExecutiveSummaryPage';
import BudgetPlanPage from './components/BudgetPlanPage';
import AnalyticToolsPage from './components/AnalyticToolsPage';
import SmartTooltip from './components/SmartTooltip';
import { DataProvider, useData } from './DataContext';
import { exportDashboardToPPT } from './utils/pptExport';
import { exportDashboardToExcel } from './utils/excelExport';
import { exportDashboardToPDF } from './utils/pdfExport';
import {
  analyzeKPI,
  analyzeChartTrend,
  analyzeExpenseBreakdown,
  analyzeNetProfitSeries,
  analyzeCashFlow,
} from './utils/smartInsightsEngine';

import { Area } from './data/financialData';

function getPresetRange(presets: Record<string, any>, months: any[], preset: DatePreset, customStart: string, customEnd: string): [number, number] {
  if (!months || months.length === 0) return [0, 0];
  if (preset === 'all') return [0, months.length - 1];
  if (preset === 'custom') {
    let si = months.findIndex(m => m.month === customStart);
    let ei = months.findIndex(m => m.month === customEnd);
    si = Math.max(0, si);
    ei = Math.max(0, ei);
    if (ei < si) {
      return [si, si];
    }
    return [si, Math.min(ei, months.length - 1)];
  }
  const fy = presets[preset];
  if (fy) {
    const s = Math.max(0, Math.min(fy.startIdx, months.length - 1));
    const e = Math.max(0, Math.min(fy.endIdx, months.length - 1));
    return [s, e];
  }
  return [0, months.length - 1];
}

function pickDefaultPreset(presets: Record<string, any>): DatePreset {
  const keys = Object.keys(presets);
  if (keys.length === 0) return 'all';
  const fullYears = keys.filter(k => k.startsWith('FY'));
  if (fullYears.length > 0) return fullYears[fullYears.length - 1];
  return keys[keys.length - 1];
}

interface AppContentProps {
  activeEntity: string;
  setActiveEntity: React.Dispatch<React.SetStateAction<string>>;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

function App() {
  const [activeEntity, setActiveEntity] = useState<string>('JUARA_GADAI');
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  return (
    <DataProvider activeEntity={activeEntity}>
      <AppContent
        activeEntity={activeEntity}
        setActiveEntity={setActiveEntity}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
    </DataProvider>
  );
}

function AppContent({ activeEntity, setActiveEntity, sidebarCollapsed, setSidebarCollapsed }: AppContentProps) {
  const [activeSection, setActiveSection] = useState('dashboard');

  const liveData = useData();
  const {
    MONTHS, FY_PRESETS, branchToArea,
    getKPIs, getRevenueExpensesSeries, getExpenseBreakdown,
    getCashFlowSeries, getNetProfitSeries, getTopExpenses,
    getPayablesReceivables, getBranchBS, getBranchFullBSAtMonth, unknownBranches,
  } = liveData;

  console.log(`[AppContent] Using MONTHS (${MONTHS?.length || 0} months). FY_PRESETS keys: ${Object.keys(FY_PRESETS || {}).join(', ')}`);
  if (MONTHS && MONTHS.length > 0) {
    console.log(`[AppContent] First month: ${MONTHS[0]?.month}, Last month: ${MONTHS[MONTHS.length - 1]?.month}`);
  }

  const [area, setArea] = useState<Area>('ALL');
  const [branch, setBranch] = useState<string>('ALL');
  
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Sync dates whenever active entity dataset changes
  useEffect(() => {
    setCustomStart(MONTHS[0].month);
    setCustomEnd(MONTHS[MONTHS.length - 1].month);
    setArea('ALL');
    setBranch('ALL');
    setDatePreset(pickDefaultPreset(FY_PRESETS));
  }, [activeEntity, MONTHS, FY_PRESETS]);

  const revenueChartRef = useRef<any>(null);
  const expenseBreakdownChartRef = useRef<any>(null);
  const netProfitChartRef = useRef<any>(null);
  const cashFlowChartRef = useRef<any>(null);

  const { isDark, toggle } = useTheme();
  const { isEnabled: insightsEnabled, toggle: toggleInsights } = useSmartInsights();

  const handleAreaChange = useCallback((a: Area) => {
    setArea(a);
    setBranch('ALL');
  }, []);

  const [startIdx, endIdx] = useMemo(
    () => getPresetRange(FY_PRESETS, MONTHS, datePreset, customStart, customEnd),
    [FY_PRESETS, MONTHS, datePreset, customStart, customEnd]
  );

  const effectiveKey = branch === 'ALL' ? (area as string) : branch;
  const areaLabel = branch !== 'ALL'
    ? `Data Area: ${branchToArea[branch] ?? area}`
    : undefined;

  const kpis = useMemo(() => getKPIs(effectiveKey, startIdx, endIdx), [getKPIs, effectiveKey, startIdx, endIdx]);
  const revExp = useMemo(() => getRevenueExpensesSeries(effectiveKey, startIdx, endIdx), [getRevenueExpensesSeries, effectiveKey, startIdx, endIdx]);
  const breakdown = useMemo(() => getExpenseBreakdown(effectiveKey, startIdx, endIdx), [getExpenseBreakdown, effectiveKey, startIdx, endIdx]);
  const cf = useMemo(() => getCashFlowSeries(effectiveKey, startIdx, endIdx), [getCashFlowSeries, effectiveKey, startIdx, endIdx]);
  const netSeries = useMemo(() => getNetProfitSeries(effectiveKey, startIdx, endIdx), [getNetProfitSeries, effectiveKey, startIdx, endIdx]);
  const topExpenses = useMemo(() => getTopExpenses(effectiveKey, startIdx, endIdx, 15), [getTopExpenses, effectiveKey, startIdx, endIdx]);
  const apar = useMemo(() => getPayablesReceivables(effectiveKey, endIdx), [getPayablesReceivables, effectiveKey, endIdx]);
  const branchBS = useMemo(
    () => branch !== 'ALL' ? getBranchBS(effectiveKey, startIdx, endIdx) : null,
    [getBranchBS, effectiveKey, branch, startIdx, endIdx]
  );

  const closingBalances = useMemo(
    () => branch !== 'ALL' ? getBranchFullBSAtMonth(effectiveKey, endIdx) : null,
    [getBranchFullBSAtMonth, effectiveKey, branch, endIdx]
  );

  const monthLabel = MONTHS[endIdx]?.label || '';

  const prevKpis = useMemo(() => {
    const length = endIdx - startIdx + 1;
    const prevStart = Math.max(0, startIdx - length);
    const prevEnd = startIdx - 1;
    if (prevEnd < 0) return null;
    return getKPIs(effectiveKey, prevStart, prevEnd);
  }, [getKPIs, effectiveKey, startIdx, endIdx]);

  const pct = (curr: number, prev: number | undefined) =>
    prev === undefined || prev === 0 ? 0 : ((curr - prev) / Math.abs(prev)) * 100;

  // ==========================================
  // SMART INSIGHTS — Revenue & OpEx breakdowns
  // ==========================================
  const revenueBreakdown = useMemo(() => {
    if (!insightsEnabled) return undefined;
    try {
      const pl = (liveData as any).profitLoss?.[(liveData as any).branchToArea?.[effectiveKey] ?? effectiveKey] ?? (liveData as any).profitLoss?.ALL;
      if (!pl?.revenue) return undefined;
      const sum = (arr: number[]) => arr.slice(startIdx, endIdx + 1).reduce((a: number, b: number) => a + b, 0);
      return {
        interestIncome: sum(pl.revenue.interestIncome || []),
        adminFee: sum(pl.revenue.adminFee || []),
        gainLelang: sum(pl.revenue.gainLelang || []),
        lateFee: sum(pl.revenue.lateFee || []),
        otherInterest: sum(pl.revenue.otherInterest || []),
      };
    } catch { return undefined; }
  }, [insightsEnabled, liveData, effectiveKey, startIdx, endIdx]);

  const opexBreakdown = useMemo(() => {
    if (!insightsEnabled) return undefined;
    try {
      const pl = (liveData as any).profitLoss?.[(liveData as any).branchToArea?.[effectiveKey] ?? effectiveKey] ?? (liveData as any).profitLoss?.ALL;
      if (!pl?.opex) return undefined;
      const sum = (arr: number[]) => arr.slice(startIdx, endIdx + 1).reduce((a: number, b: number) => a + b, 0);
      return {
        personnel: sum(pl.opex.personnel?.total || []),
        officeOps: sum(pl.opex.officeOps?.total || []),
        depreciation: sum(pl.opex.depreciation?.total || []),
        travel: sum(pl.opex.travel?.total || []),
        otherOpex: sum(pl.opex.otherOpex?.total || []),
      };
    } catch { return undefined; }
  }, [insightsEnabled, liveData, effectiveKey, startIdx, endIdx]);

  // KPI Smart Insights
  const revenueInsights = useMemo(() => insightsEnabled ? analyzeKPI({
    title: 'Total Revenue', value: kpis.totalRevenue, prevValue: prevKpis?.totalRevenue, kpis, prevKpis, revenueBreakdown
  }) : [], [insightsEnabled, kpis, prevKpis, revenueBreakdown]);

  const opexInsights = useMemo(() => insightsEnabled ? analyzeKPI({
    title: 'Total OpEx', value: kpis.opex, prevValue: prevKpis?.opex, kpis, prevKpis, opexBreakdown
  }) : [], [insightsEnabled, kpis, prevKpis, opexBreakdown]);

  const profitInsights = useMemo(() => insightsEnabled ? analyzeKPI({
    title: 'Net Profit', value: kpis.netProfit, prevValue: prevKpis?.netProfit, kpis, prevKpis
  }) : [], [insightsEnabled, kpis, prevKpis]);

  // Chart Smart Insights
  const revChartInsights = useMemo(() => insightsEnabled ? analyzeChartTrend(revExp.revenue, revExp.labels, 'Revenue') : [], [insightsEnabled, revExp]);
  const expBreakdownInsights = useMemo(() => insightsEnabled ? analyzeExpenseBreakdown(breakdown) : [], [insightsEnabled, breakdown]);
  const netProfitChartInsights = useMemo(() => insightsEnabled ? analyzeNetProfitSeries(netSeries.netProfit, netSeries.labels) : [], [insightsEnabled, netSeries]);
  const cashFlowInsights = useMemo(() => insightsEnabled ? analyzeCashFlow(cf.operating, cf.investing, cf.financing, cf.net, cf.labels) : [], [insightsEnabled, cf]);

  const getScreenAssets = useCallback(() => {
    // 1. Get canvases (charts)
    const canvases = document.querySelectorAll('.dashboard-content canvas');
    const charts: { title: string; imgData: string }[] = [];
    canvases.forEach((canvas) => {
      try {
        const imgData = (canvas as HTMLCanvasElement).toDataURL('image/png');
        let title = 'Financial Chart';
        
        // Find title upwards
        const container = canvas.closest('.chart-card, .exec-section, .data-table-card, .forecast-card, .bep-card');
        if (container) {
          const titleEl = container.querySelector('.chart-card-title, .exec-section-title, .data-table-title, h2, h3, h4');
          if (titleEl && titleEl.textContent) {
            title = titleEl.textContent.trim();
          }
        } else {
          const parent = canvas.parentElement;
          if (parent) {
            const titleEl = parent.querySelector('.chart-card-title, .exec-section-title, .data-table-title, h2, h3, h4');
            if (titleEl && titleEl.textContent) {
              title = titleEl.textContent.trim();
            }
          }
        }
        
        // Clean up emojis from titles if desired (optional, keeping for context)
        charts.push({ title, imgData });
      } catch (err) {
        console.error('Error extracting canvas:', err);
      }
    });

    // 2. Get tables
    const tables = document.querySelectorAll('.dashboard-content table');
    const extractedTables: { title: string; headers: string[]; rows: string[][] }[] = [];
    tables.forEach((table) => {
      try {
        let title = 'Table Data';
        const container = table.closest('.data-table-card, .exec-section, .chart-card, main');
        if (container) {
          const titleEl = container.querySelector('.data-table-title, .exec-section-title, h2, h3, h4');
          if (titleEl && titleEl.textContent) {
            title = titleEl.textContent.trim();
          }
        }
        
        const headers: string[] = [];
        table.querySelectorAll('thead th').forEach((th) => {
          headers.push(th.textContent?.trim() || '');
        });
        
        const rows: string[][] = [];
        table.querySelectorAll('tbody tr').forEach((tr) => {
          const rowData: string[] = [];
          tr.querySelectorAll('td').forEach((td) => {
            const childElements = td.querySelectorAll('div, span');
            if (childElements.length > 1) {
              const textParts: string[] = [];
              childElements.forEach(el => {
                // Only take leaf elements that do not contain further divs or spans to prevent duplicates
                if (el.querySelectorAll('div, span').length === 0) {
                  const txt = el.textContent?.trim() || '';
                  if (txt) textParts.push(txt);
                }
              });
              
              if (textParts.length > 0) {
                rowData.push(textParts.join(' '));
              } else {
                rowData.push(td.textContent?.trim() || '');
              }
            } else {
              rowData.push(td.textContent?.trim() || '');
            }
          });
          if (rowData.length > 0) {
            rows.push(rowData);
          }
        });
        
        extractedTables.push({ title, headers, rows });
      } catch (err) {
        console.error('Error extracting table data:', err);
      }
    });

    // 2b. Query and extract virtual tables from custom styled elements (specifically for Executive Summary page)
    // - Top 5 Cabang by Revenue
    document.querySelectorAll('.exec-section').forEach((section) => {
      try {
        const titleEl = section.querySelector('.exec-section-title');
        const titleText = titleEl?.textContent?.trim() || '';
        
        if (titleText.includes('Top 5 Cabang')) {
          const isUnderperform = titleText.includes('Underperform');
          const headers = ['Peringkat', 'Nama Cabang', 'Kode', isUnderperform ? 'Laba / Rugi Bersih' : 'Revenue'];
          const rows: string[][] = [];
          
          section.querySelectorAll('div > span + div').forEach((detailContainer, i) => {
            const row = detailContainer.parentElement;
            if (!row) return;
            const rankEl = row.querySelector('span');
            const rank = rankEl?.textContent?.trim() || `${i + 1}`;
            
            const nameEl = row.querySelector('div span');
            const fullText = nameEl?.textContent?.trim() || '';
            
            let name = fullText;
            let code = '';
            if (fullText.includes('(')) {
              const parts = fullText.split('(');
              name = parts[0].trim();
              code = parts[1].replace(')', '').trim();
            }
            
            const valEl = row.querySelector('div span:nth-child(2)');
            const val = valEl?.textContent?.trim() || '';
            
            if (name || val) {
              rows.push([rank, name, code, val]);
            }
          });
          
          if (rows.length > 0) {
            extractedTables.push({ 
              title: isUnderperform ? 'Top 5 Cabang Underperform' : 'Top 5 Cabang by Revenue', 
              headers, 
              rows 
            });
          }
        }
        
        // - Profit Margins (Fallback for historical datasets)
        if (titleText.includes('Profit Margins')) {
          const headers = ['Margin Metric', 'Percentage'];
          const rows: string[][] = [];
          
          section.querySelectorAll('div[style*="padding: 8px 0"] > div, div[style*="padding:8px 0"] > div').forEach((row) => {
            const spans = row.querySelectorAll('span');
            if (spans.length >= 2) {
              const label = spans[0].textContent?.trim() || '';
              const val = spans[1].textContent?.trim() || '';
              rows.push([label, val]);
            }
          });
          
          if (rows.length === 0) {
            section.querySelectorAll('div > div').forEach((row) => {
              const spans = row.querySelectorAll('span');
              if (spans.length === 2) {
                const label = spans[0].textContent?.trim() || '';
                const val = spans[1].textContent?.trim() || '';
                rows.push([label, val]);
              }
            });
          }
          
          if (rows.length > 0) {
            extractedTables.push({ title: 'Profit Margins Analysis', headers, rows });
          }
        }
      } catch (err) {
        console.error('Error extracting virtual exec tables:', err);
      }
    });

    // - Financial Health Score Factors
    try {
      const healthFactorsEl = document.querySelector('.exec-health-factors');
      if (healthFactorsEl) {
        const headers = ['Faktor Kesehatan Keuangan', 'Nilai / Status'];
        const rows: string[][] = [];
        healthFactorsEl.querySelectorAll('.exec-factor').forEach((factor) => {
          const labelEl = factor.querySelector('.exec-factor-label');
          const valEl = factor.querySelector('.exec-factor-value');
          if (labelEl && valEl) {
            rows.push([labelEl.textContent?.trim() || '', valEl.textContent?.trim() || '']);
          }
        });
        
        if (rows.length > 0) {
          extractedTables.push({ title: 'Financial Health Score Factors', headers, rows });
        }
      }
    } catch (err) {
      console.error('Error extracting health score virtual table:', err);
    }

    // 3. Get screen KPIs
    const extractedKPIs: { title: string; value: string; change?: string }[] = [];
    document.querySelectorAll('.kpi-card, .exec-kpi-card').forEach((card) => {
      try {
        const titleEl = card.querySelector('.kpi-card-title, .exec-kpi-title');
        const valEl = card.querySelector('.kpi-card-value, .exec-kpi-value');
        const changeEl = card.querySelector('.kpi-card-change, span');
        if (titleEl && valEl && titleEl.textContent && valEl.textContent) {
          const change = changeEl?.textContent?.trim();
          extractedKPIs.push({
            title: titleEl.textContent.trim(),
            value: valEl.textContent.trim(),
            change: change && (change.includes('%') || change.includes('▲') || change.includes('▼')) ? change : undefined,
          });
        }
      } catch (err) {
        console.error('Error extracting KPI card:', err);
      }
    });

    // If no standard KPIs found, look for Grid balance sheet ratios/KPIs
    if (extractedKPIs.length === 0) {
      const bsKpiGrid = document.querySelector('.dashboard-content div[style*="grid-template-columns"]');
      if (bsKpiGrid) {
        bsKpiGrid.querySelectorAll('div[style*="background-color"]').forEach((item) => {
          const divList = item.querySelectorAll('div');
          if (divList.length >= 2) {
            const title = divList[0].textContent?.trim();
            const value = divList[1].textContent?.trim();
            if (title && value) {
              extractedKPIs.push({ title, value });
            }
          }
        });
      }
    }

    return { charts, tables: extractedTables, kpis: extractedKPIs };
  }, []);

  const handleExportPDF = useCallback(() => {
    const prefix = activeEntity === 'JUARA_GADAI' ? 'JG_' : 'SG_';
    const filename = `${prefix}${area}${branch !== 'ALL' ? `_${branch}` : ''}_${MONTHS[startIdx]?.month || ''}_to_${MONTHS[endIdx]?.month || ''}.pdf`;
    const { charts, tables, kpis } = getScreenAssets();
    
    exportDashboardToPDF({
      area,
      branch,
      activeEntity,
      activeSection,
      filename,
      periodLabel: `${MONTHS[startIdx]?.label || ''} – ${MONTHS[endIdx]?.label || ''}`,
      extractedKPIs: kpis,
      charts,
      tables,
    });
  }, [activeEntity, area, branch, startIdx, endIdx, activeSection, MONTHS, getScreenAssets]);

  const handleExportExcel = useCallback(() => {
    const sectionMap: { [key: string]: string } = {
      'dashboard': 'dashboard',
      'revenue': 'revenue',
      'expenses': 'expenses',
      'profit_loss': 'profit_loss',
      'balance': 'balance',
      'cashflow': 'dashboard',
      'forecast': 'forecast',
      'bep': 'bep',
    };
    const section = sectionMap[activeSection] || 'dashboard';
    const { tables } = getScreenAssets();

    exportDashboardToExcel({
      area,
      branch,
      startIdx,
      endIdx,
      sectionName: section,
      activeEntity,
      activeData: liveData,
      domTables: tables,
    });
  }, [area, branch, startIdx, endIdx, activeSection, activeEntity, liveData, getScreenAssets]);

  const handleExportPPT = useCallback(() => {
    const { charts, tables, kpis } = getScreenAssets();
    exportDashboardToPPT({
      area,
      branch,
      activeEntity,
      activeSection,
      periodLabel: `${MONTHS[startIdx]?.label || ''} – ${MONTHS[endIdx]?.label || ''}`,
      extractedKPIs: kpis,
      charts,
      tables,
    });
  }, [area, branch, startIdx, endIdx, activeSection, activeEntity, MONTHS, getScreenAssets]);

  const dataRangeLabel = MONTHS.length > 0
    ? `${MONTHS[0].label} – ${MONTHS[MONTHS.length - 1].label}`
    : '';

  // ==========================================
  // INJECT GLOBAL COPY BUTTONS
  // ==========================================
  useEffect(() => {
    const injectCopyButtons = () => {
      const dashboardContent = document.querySelector('.dashboard-content');
      if (!dashboardContent) return;
      
      // 1. Target canvases (Charts)
      dashboardContent.querySelectorAll('.chart-card, .exec-section, .forecast-card').forEach((card) => {
        if (card.querySelector('.global-copy-btn')) return;
        const canvas = card.querySelector('canvas');
        if (!canvas) return;
        
        createCopyButton(card as HTMLElement, () => {
          const btn = card.querySelector('.global-copy-btn') as HTMLButtonElement;
          copyCanvasToClipboard(canvas, btn);
        });
      });
      
      // 2. Target standard tables
      dashboardContent.querySelectorAll('.data-table-card').forEach((card) => {
        if (card.querySelector('.global-copy-btn')) return;
        const table = card.querySelector('table');
        if (!table) return;
        
        createCopyButton(card as HTMLElement, () => {
          const btn = card.querySelector('.global-copy-btn') as HTMLButtonElement;
          copyTableToClipboard(table, btn);
        });
      });
      
      // 3. Target KPI Cards
      dashboardContent.querySelectorAll('.kpi-card, .exec-kpi-card').forEach((card) => {
        if (card.querySelector('.global-copy-btn')) return;
        
        createCopyButton(card as HTMLElement, () => {
          const btn = card.querySelector('.global-copy-btn') as HTMLButtonElement;
          copyKPICardToClipboard(card as HTMLElement, btn);
        });
      });
      
      // 4. Target Executive Summary virtual lists (Top 5 Cabang by Revenue, Profit Margins)
      dashboardContent.querySelectorAll('.exec-section').forEach((section) => {
        if (section.querySelector('.global-copy-btn')) return;
        if (section.querySelector('canvas')) return; // handled by step 1
        
        const titleEl = section.querySelector('.exec-section-title');
        const titleText = titleEl?.textContent?.trim() || '';
        
        if (titleText.includes('Top 5 Cabang') || titleText.includes('Profit Margins')) {
          createCopyButton(section as HTMLElement, () => {
            const btn = section.querySelector('.global-copy-btn') as HTMLButtonElement;
            copyCustomSectionToClipboard(section as HTMLElement, btn);
          });
        }
      });
      
      // 5. Target Health Score Factors panel
      dashboardContent.querySelectorAll('.exec-health-factors').forEach((el) => {
        if (el.querySelector('.global-copy-btn')) return;
        
        createCopyButton(el as HTMLElement, () => {
          const btn = el.querySelector('.global-copy-btn') as HTMLButtonElement;
          copyCustomSectionToClipboard(el as HTMLElement, btn);
        });
      });
    };
    
    const createCopyButton = (parent: HTMLElement, onClick: () => void) => {
      if (parent.querySelector('.global-copy-btn')) return;
      
      const computedStyle = window.getComputedStyle(parent);
      if (computedStyle.position === 'static') {
        parent.style.position = 'relative';
      }
      
      const btn = document.createElement('button');
      btn.className = 'global-copy-btn';
      btn.setAttribute('type', 'button');
      btn.setAttribute('title', 'Salin Konten ke Clipboard');
      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      `;
      
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      });
      
      parent.appendChild(btn);
    };
    
    // Initial run
    injectCopyButtons();
    
    // Run periodically to catch route swaps / dynamic list renders
    const intervalId = setInterval(injectCopyButtons, 1200);
    
    // Immediate injection on click interaction
    const clickListener = () => setTimeout(injectCopyButtons, 100);
    document.addEventListener('click', clickListener);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('click', clickListener);
    };
  }, [activeSection, activeEntity]);

  return (
    <div className="app-layout">
        {/* Sidebar with Profile Selector */}
        <Sidebar
          activeSection={activeSection} 
          onSectionChange={setActiveSection} 
          activeEntity={activeEntity}
          onEntityChange={setActiveEntity}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="top-bar">
          <div className="top-bar-left">
            <h1 className="top-bar-title">Financial Dashboard</h1>
            <p className="top-bar-subtitle">
              {activeEntity === 'JUARA_GADAI' ? 'Juara Gadai Group' : 'Sahabat Gadai / Juara Gadai'} • {MONTHS.length} bulan • {dataRangeLabel}
            </p>
          </div>
          <div className="top-bar-right">
            <div className="live-data-badge" style={{ backgroundColor: activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : '' }}>
              <div className="live-data-dot" style={{ backgroundColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '' }} />
              <span className="live-data-text" style={{ color: activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '' }}>
                {activeEntity === 'JUARA_GADAI' ? 'Google Drive Link' : 'Live Data'}
              </span>
            </div>
            <button
              className={`smart-insights-toggle${insightsEnabled ? ' active' : ''}`}
              onClick={toggleInsights}
              title={insightsEnabled ? 'Smart Insights: AKTIF — Hover elemen untuk analisa' : 'Smart Insights: NONAKTIF — Klik untuk mengaktifkan'}
            >
              💡
            </button>
            <button
              className="theme-toggle-btn"
              onClick={toggle}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
        </header>

        {unknownBranches.length > 0 && (
          <div className="unknown-branch-banner">
            ⚠️ {unknownBranches.length} kode cabang belum terdaftar di MASTER CABANG:{' '}
            <strong>{unknownBranches.join(', ')}</strong> — tambahkan ke sheet MASTER CABANG
          </div>
        )}
        <FiltersBar
          area={area}
          onAreaChange={handleAreaChange}
          branch={branch}
          onBranchChange={setBranch}
          datePreset={datePreset}
          onDatePresetChange={setDatePreset}
          startMonth={customStart}
          endMonth={customEnd}
          onStartMonthChange={setCustomStart}
          onEndMonthChange={setCustomEnd}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          onExportPPT={handleExportPPT}
        />

        <div className="dashboard-content">
          {activeSection === 'dashboard' && (
            <>
              <div className="kpi-grid">
                <KPICard
                  title="Total Revenue"
                  value={kpis.totalRevenue}
                  change={pct(kpis.totalRevenue, prevKpis?.totalRevenue)}
                  changeLabel="vs periode lalu"
                  type="revenue" icon="💰" animationDelay={1}
                  onClick={() => setActiveSection('revenue')}
                  tooltip="Klik untuk detail Revenue"
                  smartInsights={revenueInsights}
                />
                <KPICard
                  title="Total OpEx"
                  value={kpis.opex}
                  change={pct(kpis.opex, prevKpis?.opex)}
                  changeLabel="vs periode lalu"
                  type="expenses" icon="📉" animationDelay={2}
                  onClick={() => setActiveSection('expenses')}
                  tooltip="Klik untuk detail Expenses"
                  smartInsights={opexInsights}
                />
                <KPICard
                  title="Net Profit"
                  value={kpis.netProfit}
                  change={pct(kpis.netProfit, prevKpis?.netProfit)}
                  changeLabel="vs periode lalu"
                  type="profit" icon="📊" animationDelay={3}
                  onClick={() => setActiveSection('profit_loss')}
                  tooltip="Klik untuk Laporan Profit and Loss"
                  smartInsights={profitInsights}
                />
                {branchBS && (
                  <>
                    <KPICard
                      title="Petty Cash"
                      value={branchBS.cashBank}
                      type="revenue"
                      icon="💵"
                      animationDelay={5}
                    />
                    <KPICard
                      title="Piutang (AR)"
                      value={branchBS.receivables}
                      type="cashflow"
                      icon="📋"
                      animationDelay={6}
                    />
                  </>
                )}
                {closingBalances && (
                  <>
                    <KPICard
                      title="Saldo Akhir Petty Cash"
                      value={closingBalances.currentAssets.cashBank}
                      type="revenue"
                      icon="🏦"
                      animationDelay={7}
                    />
                    <KPICard
                      title="Saldo Akhir AR"
                      value={closingBalances.currentAssets.receivables}
                      type="cashflow"
                      icon="📑"
                      animationDelay={8}
                    />
                  </>
                )}
              </div>

              <div className="charts-row">
                <SmartTooltip insights={revChartInsights} title="Revenue & Expenses">
                  <RevenueExpensesChart ref={revenueChartRef} labels={revExp.labels} revenue={revExp.revenue} expenses={revExp.opex} />
                </SmartTooltip>
                <SmartTooltip insights={expBreakdownInsights} title="Expense Breakdown">
                  <ExpenseBreakdownChart ref={expenseBreakdownChartRef} data={breakdown} activeEntity={activeEntity} />
                </SmartTooltip>
              </div>

              <div className="charts-row-equal">
                <SmartTooltip insights={netProfitChartInsights} title="Net Profit Trend">
                  <NetProfitChart ref={netProfitChartRef} labels={netSeries.labels} data={netSeries.netProfit} />
                </SmartTooltip>
                <SmartTooltip insights={cashFlowInsights} title="Cash Flow">
                  <CashFlowChart
                    ref={cashFlowChartRef}
                    labels={cf.labels}
                    operating={cf.operating}
                    investing={cf.investing}
                    financing={cf.financing}
                    net={cf.net}
                    areaLabel={areaLabel}
                  />
                </SmartTooltip>
              </div>

              <AccountsPayableReceivable data={apar} monthLabel={monthLabel} areaLabel={areaLabel} />

              <TransactionTable items={topExpenses} periodLabel={`${MONTHS[startIdx]?.label || ''} – ${MONTHS[endIdx]?.label || ''}`} />
            </>
          )}
          {activeSection === 'revenue' && (
            <RevenueDetailPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} activeEntity={activeEntity} />
          )}
          {activeSection === 'expenses' && (
            <ExpensesDetailPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} activeEntity={activeEntity} />
          )}
          {activeSection === 'balance' && (
            <BalanceSheetPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} activeEntity={activeEntity} />
          )}
          {activeSection === 'cashflow' && (
            <CashFlowPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} />
          )}
          {activeSection === 'profit_loss' && (
            <ProfitLossPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} activeEntity={activeEntity} />
          )}
          {activeSection === 'forecast' && (
            <ForecastPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} />
          )}
          {activeSection === 'bep' && (
            <BEPPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} />
          )}
          {activeSection === 'executive' && (
            <ExecutiveSummaryPage area={area} branch={branch} startIdx={startIdx} endIdx={endIdx} onNavigate={setActiveSection} activeEntity={activeEntity} />
          )}
          {activeSection === 'budget_plan' && (
            <BudgetPlanPage activeEntity={activeEntity} startIdx={startIdx} endIdx={endIdx} />
          )}
          {activeSection === 'analytics' && (
            <AnalyticToolsPage activeEntity={activeEntity} startIdx={startIdx} endIdx={endIdx} globalArea={area} globalBranch={branch} />
          )}
          {activeSection === 'gl_lookup' && (
            <GLLookupPage initialArea={area} initialBranch={branch} activeEntity={activeEntity} />
          )}
          {activeSection === 'database' && (
            <DatabasePage activeEntity={activeEntity} />
          )}
        </div>
      </main>
    </div>
  );
}

// ==========================================
// GLOBAL CLIPBOARD COPY HELPERS
// ==========================================

function getCellCleanText(td: HTMLElement): string {
  const childElements = td.querySelectorAll('div, span');
  if (childElements.length > 1) {
    const textParts: string[] = [];
    childElements.forEach(el => {
      if (el.querySelectorAll('div, span').length === 0) {
        const txt = el.textContent?.trim() || '';
        if (txt) textParts.push(txt);
      }
    });
    if (textParts.length > 0) {
      return textParts.join(' ');
    }
  }
  return td.textContent?.trim() || '';
}

function showSuccessTooltip(btn: HTMLButtonElement, message: string) {
  const parent = btn.parentElement;
  if (parent) {
    parent.querySelectorAll('.copy-tooltip').forEach(el => el.remove());
  }

  const tooltip = document.createElement('div');
  tooltip.className = 'copy-tooltip';
  tooltip.textContent = message;
  
  parent?.appendChild(tooltip);
  
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      tooltip.classList.add('show');
    });
  });
  
  const originalHTML = btn.innerHTML;
  btn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="color: var(--emerald-light);">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  `;
  btn.style.borderColor = 'var(--emerald)';
  btn.style.background = 'var(--emerald-glow)';
  
  setTimeout(() => {
    tooltip.classList.remove('show');
    setTimeout(() => tooltip.remove(), 250);
    
    btn.innerHTML = originalHTML;
    btn.style.borderColor = '';
    btn.style.background = '';
  }, 2000);
}

function fallbackTextCopy(text: string, btn: HTMLButtonElement, successMsg: string = "Teks Tersalin!") {
  navigator.clipboard.writeText(text).then(() => {
    showSuccessTooltip(btn, successMsg);
  }).catch(err => {
    console.error("Gagal menyalin teks:", err);
  });
}

function copyCanvasToClipboard(canvas: HTMLCanvasElement, btn: HTMLButtonElement) {
  try {
    canvas.toBlob((blob) => {
      if (blob) {
        try {
          const item = new ClipboardItem({ [blob.type]: blob });
          navigator.clipboard.write([item]).then(() => {
            showSuccessTooltip(btn, "Grafik Tersalin (Gambar)!");
          }).catch(err => {
            console.warn("ClipboardItem write failed, trying fallback:", err);
            fallbackTextCopy("[Financial Chart Image]", btn, "Gambar tidak didukung browser!");
          });
        } catch (itemErr) {
          console.warn("ClipboardItem creation failed, trying fallback:", itemErr);
          fallbackTextCopy("[Financial Chart Image]", btn, "Browser tidak mendukung salin gambar!");
        }
      } else {
        fallbackTextCopy("[Financial Chart Image]", btn, "Gagal membuat blob gambar!");
      }
    }, "image/png");
  } catch (err) {
    console.error("Gagal mengekspor canvas:", err);
    fallbackTextCopy("[Financial Chart Image]", btn, "Gagal memproses gambar!");
  }
}

function copyTableToClipboard(table: HTMLTableElement, btn: HTMLButtonElement) {
  try {
    let html = `<table border="1" style="border-collapse: collapse; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px;">`;
    let text = "";
    
    const headers = table.querySelectorAll('thead th');
    if (headers.length > 0) {
      html += `<tr style="background-color: #0f172a; color: #ffffff; font-weight: bold;">`;
      const headerTexts: string[] = [];
      headers.forEach(th => {
        const cleanVal = th.textContent?.trim() || "";
        html += `<th style="padding: 10px 12px; border: 1px solid #cbd5e1; text-align: left;">${cleanVal}</th>`;
        headerTexts.push(cleanVal);
      });
      html += `</tr>`;
      text += headerTexts.join("\t") + "\n";
    }
    
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach((tr) => {
      html += `<tr>`;
      const rowTexts: string[] = [];
      const cells = tr.querySelectorAll('td');
      cells.forEach(td => {
        const cleanVal = getCellCleanText(td as HTMLElement);
        
        let cellStyle = "padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left;";
        if (td.querySelector('.amount-positive')) {
          cellStyle += " color: #10b981; font-weight: bold;";
        } else if (td.querySelector('.amount-negative')) {
          cellStyle += " color: #ef4444; font-weight: bold;";
        }
        
        html += `<td style="${cellStyle}">${cleanVal}</td>`;
        rowTexts.push(cleanVal);
      });
      html += `</tr>`;
      text += rowTexts.join("\t") + "\n";
    });
    
    html += `</table>`;
    
    try {
      const htmlBlob = new Blob([html], { type: "text/html" });
      const textBlob = new Blob([text], { type: "text/plain" });
      const item = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob
      });
      
      navigator.clipboard.write([item]).then(() => {
        showSuccessTooltip(btn, "Tabel Tersalin (Excel)!");
      }).catch(err => {
        console.warn("Gagal menulis tabel HTML ke clipboard, fallback ke teks:", err);
        fallbackTextCopy(text, btn, "Tabel Tersalin (Teks)!");
      });
    } catch (e) {
      fallbackTextCopy(text, btn, "Tabel Tersalin (Teks)!");
    }
  } catch (err) {
    console.error("Gagal memproses tabel:", err);
  }
}

function copyCustomSectionToClipboard(section: HTMLElement, btn: HTMLButtonElement) {
  try {
    const titleEl = section.querySelector('.exec-section-title, h2, h3, h4');
    const titleText = titleEl?.textContent?.trim() || '';
    
    let headers: string[] = [];
    let rows: string[][] = [];
    let title = "Data";
    
    if (titleText.includes('Top 5 Cabang')) {
      const isUnderperform = titleText.includes('Underperform');
      title = isUnderperform ? 'Top 5 Cabang Underperform' : 'Top 5 Cabang by Revenue';
      headers = ['Peringkat', 'Nama Cabang', 'Kode', isUnderperform ? 'Laba / Rugi Bersih' : 'Revenue'];
      
      section.querySelectorAll('div > span + div').forEach((detailContainer, i) => {
        const row = detailContainer.parentElement;
        if (!row) return;
        const rankEl = row.querySelector('span');
        const rank = rankEl?.textContent?.trim() || `${i + 1}`;
        const nameEl = row.querySelector('div span');
        const fullText = nameEl?.textContent?.trim() || '';
        let name = fullText;
        let code = '';
        if (fullText.includes('(')) {
          const parts = fullText.split('(');
          name = parts[0].trim();
          code = parts[1].replace(')', '').trim();
        }
        const valEl = row.querySelector('div span:nth-child(2)');
        const val = valEl?.textContent?.trim() || '';
        if (name || val) {
          rows.push([rank, name, code, val]);
        }
      });
    } else if (titleText.includes('Profit Margins')) {
      title = 'Profit Margins Analysis';
      headers = ['Margin Metric', 'Percentage'];
      section.querySelectorAll('div[style*="padding: 8px 0"] > div, div[style*="padding:8px 0"] > div').forEach((row) => {
        const spans = row.querySelectorAll('span');
        if (spans.length >= 2) {
          const label = spans[0].textContent?.trim() || '';
          const val = spans[1].textContent?.trim() || '';
          rows.push([label, val]);
        }
      });
      if (rows.length === 0) {
        section.querySelectorAll('div > div').forEach((row) => {
          const spans = row.querySelectorAll('span');
          if (spans.length === 2) {
            const label = spans[0].textContent?.trim() || '';
            const val = spans[1].textContent?.trim() || '';
            rows.push([label, val]);
          }
        });
      }
    } else if (section.classList.contains('exec-health-factors') || titleText.includes('Health Score') || section.querySelector('.exec-health-factors')) {
      title = 'Financial Health Score Factors';
      headers = ['Faktor Kesehatan Keuangan', 'Nilai / Status'];
      const healthFactorsEl = section.classList.contains('exec-health-factors') ? section : section.querySelector('.exec-health-factors');
      if (healthFactorsEl) {
        healthFactorsEl.querySelectorAll('.exec-factor').forEach((factor) => {
          const labelEl = factor.querySelector('.exec-factor-label');
          const valEl = factor.querySelector('.exec-factor-value');
          if (labelEl && valEl) {
            rows.push([labelEl.textContent?.trim() || '', valEl.textContent?.trim() || '']);
          }
        });
      }
    }
    
    if (rows.length > 0) {
      let html = `<table border="1" style="border-collapse: collapse; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px;">`;
      html += `<tr style="background-color: #0f172a; color: #ffffff; font-weight: bold;"><th colspan="${headers.length}" style="padding: 10px 12px; border: 1px solid #cbd5e1; text-align: center;">${title}</th></tr>`;
      html += `<tr style="background-color: #f1f5f9; font-weight: bold;">`;
      headers.forEach(h => {
        html += `<th style="padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left;">${h}</th>`;
      });
      html += `</tr>`;
      
      let text = title + "\n" + headers.join("\t") + "\n";
      
      rows.forEach(r => {
        html += `<tr>`;
        r.forEach(val => {
          html += `<td style="padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left;">${val}</td>`;
        });
        html += `</tr>`;
        text += r.join("\t") + "\n";
      });
      
      html += `</table>`;
      
      try {
        const htmlBlob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([text], { type: "text/plain" });
        const item = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob
        });
        
        navigator.clipboard.write([item]).then(() => {
          showSuccessTooltip(btn, "Daftar Tersalin (Excel)!");
        }).catch(err => {
          console.warn("Gagal menyalin custom section HTML, fallback ke teks:", err);
          fallbackTextCopy(text, btn, "Daftar Tersalin (Teks)!");
        });
      } catch (e) {
        fallbackTextCopy(text, btn, "Daftar Tersalin (Teks)!");
      }
    } else {
      const text = section.innerText || section.textContent || "";
      fallbackTextCopy(text.trim(), btn, "Teks Tersalin!");
    }
  } catch (err) {
    console.error("Gagal menyalin custom section:", err);
  }
}

function copyKPICardToClipboard(card: HTMLElement, btn: HTMLButtonElement) {
  try {
    const titleEl = card.querySelector('.kpi-card-title, .exec-kpi-title');
    const valEl = card.querySelector('.kpi-card-value, .exec-kpi-value');
    const changeEl = card.querySelector('.kpi-card-change, span');
    
    const title = titleEl?.textContent?.trim() || 'KPI';
    const value = valEl?.textContent?.trim() || '';
    const change = changeEl && (changeEl.textContent?.includes('%') || changeEl.textContent?.includes('▲') || changeEl.textContent?.includes('▼')) 
      ? changeEl.textContent.trim() 
      : '';
    
    let text = `${title}: ${value}`;
    if (change) {
      text += ` (${change})`;
    }
    
    let html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 14px; border: 1px solid #e2e8f0; border-radius: 8px; background-color: #f8fafc; max-width: 280px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
        <div style="font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: bold; margin-bottom: 6px; letter-spacing: 0.5px;">${title}</div>
        <div style="font-size: 22px; font-weight: bold; color: #0f172a; margin-bottom: 4px; letter-spacing: -0.5px;">${value}</div>
        ${change ? `<div style="font-size: 12px; color: ${change.includes('▲') || change.includes('+') ? '#10b981' : '#ef4444'}; font-weight: bold;">${change}</div>` : ''}
      </div>
    `;
    
    try {
      const htmlBlob = new Blob([html], { type: "text/html" });
      const textBlob = new Blob([text], { type: "text/plain" });
      const item = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob
      });
      
      navigator.clipboard.write([item]).then(() => {
        showSuccessTooltip(btn, "KPI Tersalin!");
      }).catch(err => {
        console.warn("Gagal menyalin KPI HTML, fallback ke teks:", err);
        fallbackTextCopy(text, btn, "KPI Tersalin!");
      });
    } catch (e) {
      fallbackTextCopy(text, btn, "KPI Tersalin!");
    }
  } catch (err) {
    console.error("Gagal menyalin KPI card:", err);
  }
}

export default App;

